import isEmpty from 'lodash/isEmpty';

import { TaskData, TaskListQuery, TaskStatus, WithNullable } from 'chaika-frontend-types/frontend-cms';

import { isDefined } from '@utils/isDefined';
import { nullToUndefined } from '@utils/nullToUndefined';

import { LongList } from '@store/abstract/LongList';

export interface TaskFilterParams {
    medicalCaseId?: string;
    medicalCaseIds?: string[];
    patientId?: string;
    executorIds?: string[];
    relatedUser?: string;
    status?: TaskStatus[];
    excludedStatus?: TaskStatus[];
}

type TaskNullableForUpdate = 'actualExecutorId' | 'patientId' | 'medicalCaseIds' | 'parentEntryId';

export class TaskList extends LongList<TaskData, TaskData, TaskFilterParams> {
    public searchParams: TaskListQuery = {};
    private medicalCaseIdsLoadedTasks = new Set<string>();
    private patientIdsLoadedTasks = new Set<string>();

    public isTaskLoadedForMedicalCase(medicalCaseId: string): boolean {
        return this.medicalCaseIdsLoadedTasks.has(medicalCaseId);
    }

    public isTaskLoadedForPatient(patientId: string): boolean {
        return this.patientIdsLoadedTasks.has(patientId);
    }

    public addTaskLoadedPatientId(patientId: string): void {
        this.patientIdsLoadedTasks.add(patientId);
    }

    public addTaskLoadedMedicalCaseId(medicalCaseId: string): void {
        this.medicalCaseIdsLoadedTasks.add(medicalCaseId);
    }

    public addTaskLoadedMedicalCaseIds(medicalCaseIds: string[]): void {
        medicalCaseIds.forEach(medicalCaseId => {
            this.addTaskLoadedMedicalCaseId(medicalCaseId);
        });
    }

    public setSearchParams(params: TaskListQuery): void {
        this.searchParams = params;
    }

    public update(id: string, updateParams: WithNullable<Partial<TaskData>, TaskNullableForUpdate>): void {
        const current = this.get(id);
        if (!current) {
            return;
        }

        this.list.set(id, this.create({ ...current, ...updateParams }));
    }

    protected create(params: WithNullable<TaskData, TaskNullableForUpdate>): TaskData {
        return nullToUndefined(params) as TaskData;
    }

    protected filterValue(value: TaskData, filterParams: TaskFilterParams): boolean {
        const filterByMedicalCaseId = isDefined(filterParams.medicalCaseId)
            ? (value.medicalCaseIds ?? []).includes(filterParams.medicalCaseId)
            : true;
        const filterMedicalCaseIds = filterParams?.medicalCaseIds ?? [];
        const valueMedicalCaseIds = value?.medicalCaseIds ?? [];
        const filterByMedicalCaseIds =
            !isEmpty(filterMedicalCaseIds) && !isEmpty(valueMedicalCaseIds)
                ? valueMedicalCaseIds.some(id => filterMedicalCaseIds.includes(id))
                : true;

        const filterByPatientId = isDefined(filterParams.patientId) ? value.patientId === filterParams.patientId : true;

        const filterByRelatedUserId = isDefined(filterParams.relatedUser)
            ? [value.authorId, value.requesterId, ...value.executorIds, value.actualExecutorId].includes(
                  filterParams.relatedUser,
              )
            : true;

        const filterByStatus = isDefined(filterParams.status) ? filterParams.status.includes(value.status) : true;

        const filterByExcludedStatus = isDefined(filterParams.excludedStatus)
            ? !filterParams.excludedStatus.includes(value.status)
            : true;
        const filterExecutorIds = filterParams.executorIds;
        const filterByExecutorIds = isDefined(filterExecutorIds)
            ? value.executorIds?.some(id => filterExecutorIds.includes(id))
            : true;

        return (
            filterByMedicalCaseId &&
            filterByRelatedUserId &&
            filterByStatus &&
            filterByExcludedStatus &&
            filterByExecutorIds &&
            filterByMedicalCaseIds &&
            filterByPatientId
        );
    }
}
